/**
 * The two behaviours the task board's chrome needs: a pane you can drag wider, and columns
 * paged by a button on each edge.
 *
 * Their own module so they can be tested against real Alpine. The carousel shipped broken
 * because of something no amount of reading the code would have shown: `x-ref` registers on
 * the NEAREST `x-data`, and refs resolve downwards only. The board carries its own
 * `x-data="taskLanes()"` for the drag, so a `x-ref="track"` on it belongs to that component
 * and the wrapper around it cannot see it — `this.$refs.track` is undefined, and Alpine
 * reports it as "Cannot read properties of undefined" from inside init().
 *
 * So the track is found by ATTRIBUTE, which crosses component boundaries because the DOM does.
 */

/** How the carousel finds the scroller, deliberately not an x-ref. @see the note above */
export const TRACK = '[data-board-track]';

/** The gap between columns, matching `.tasks-board { gap }` — one page is one column. */
const COLUMN_GAP = 14;

/**
 * A panel you can drag wider, and a board that gives up the room.
 *
 * The width lives in a CSS custom property rather than in a style attribute on each side, so
 * one number moves both in the same frame. It is written straight to the DOM during the drag
 * and only committed to Alpine (and to storage) on release: a re-render per pointermove is
 * what makes a resize feel like it is fighting back.
 */
export function splitPane(key, initial = 400, min = 300, max = 760) {
    return {
        width: initial,
        dragging: false,

        /**
         * The x-data element, captured once.
         *
         * The same trap the carousel fell into: `$el` is the element of the CURRENT evaluation
         * context. The drag handle carries the pointerdown and keydown listeners, so inside
         * them `$el` is the GRIP — and the width would be written to the handle instead of to
         * the container that has to act on it. Nothing errors; the panel simply never moves.
         */
        root: null,

        init() {
            this.root = this.$el;

            const saved = readNumber(`zar-split-${key}`);
            if (saved !== null) {
                this.width = this.clamp(saved);
            }

            this.apply();

            // The ceiling depends on the window, so a panel dragged wide on a desktop must not
            // stay wider than the screen it is later opened on.
            this.onResize = () => {
                const next = this.clamp(this.width);
                if (next !== this.width) {
                    this.width = next;
                    this.apply();
                }
            };
            window.addEventListener('resize', this.onResize, { passive: true });
        },

        destroy() {
            window.removeEventListener('resize', this.onResize);
        },

        clamp(value) {
            // Never more than two thirds: a panel that leaves the board a sliver has stopped
            // being a panel beside a board.
            const ceiling = Math.min(max, Math.round((window.innerWidth || 1200) * 0.66));
            return Math.max(min, Math.min(Math.round(value), Math.max(min, ceiling)));
        },

        apply() {
            this.root?.style?.setProperty('--pane-w', `${this.width}px`);
        },

        start(event) {
            this.dragging = true;
            const startX = event.clientX;
            const startWidth = this.width;
            let latest = startWidth;

            // Dragging leftwards makes the panel WIDER, because it is anchored to the right.
            const move = (e) => {
                latest = this.clamp(startWidth + (startX - e.clientX));
                this.root?.style?.setProperty('--pane-w', `${latest}px`);
            };

            const up = () => {
                window.removeEventListener('pointermove', move);
                this.dragging = false;
                this.width = latest;
                writeNumber(`zar-split-${key}`, latest);
            };

            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up, { once: true });
            event.preventDefault?.();
        },

        /** The keyboard equivalent, because a handle nobody can tab to is not a control. */
        nudge(delta) {
            this.width = this.clamp(this.width + delta);
            this.apply();
            writeNumber(`zar-split-${key}`, this.width);
        },
    };
}

/**
 * Columns paged by a button on each edge, instead of a horizontal scrollbar.
 *
 * A scrollbar under a board is both hard to hit and easy to miss — it is off the bottom of the
 * columns, which are the tall thing you are actually reading. These sit down the sides at full
 * height, so the target is the whole edge.
 *
 * It still scrolls: the buttons move the same overflow container, so a trackpad swipe and the
 * keyboard keep working and the bar is only hidden, never removed.
 */
export function boardCarousel() {
    return {
        atStart: true,
        atEnd: false,
        overflowing: false,

        /** The x-data element, captured once. @see init for why it is not read on demand. */
        root: null,

        track() {
            return this.root?.querySelector(TRACK) ?? null;
        },

        /**
         * Bring one card's column into view, centred.
         *
         * Clicking a card opens the panel, and the panel takes width from the board — so a card
         * near the right-hand edge can be pushed out of the track by the very click that opened
         * it. The panel then describes something the reader has to hunt for with the pagers.
         *
         * Deferred by two frames: the first is Livewire's morph, the second is the layout that
         * follows the panel appearing. Measuring before either has happened centres the column
         * against the width the board had a moment ago.
         *
         * Centred rather than merely scrolled-into-view, because "just in view" leaves the card
         * against an edge that the next thing to open will push it off again.
         */
        reveal(id) {
            requestAnimationFrame(() => requestAnimationFrame(() => {
                const track = this.track();
                const card = this.root?.querySelector(`[data-kanban-card="${id}"]`);
                const column = card?.closest('[data-board-column]');

                if (! track || ! column) {
                    return;
                }

                const left = centreOffsetFor(track, column);

                if (track.scrollTo) {
                    track.scrollTo({ left, behavior: 'smooth' });
                } else {
                    track.scrollLeft = left;
                }
            }));
        },

        init() {
            /*
             * `$el` is the element of the CURRENT evaluation context, not the component root.
             *
             * In here it is the x-data element, so reading it on demand looked right and the
             * pagers appeared and disabled correctly. But inside `x-on:click` it is the BUTTON,
             * so the same lookup found no track and every click returned early — visible,
             * enabled, and doing nothing.
             *
             * Captured here, while the context IS the root.
             */
            this.root = this.$el;

            this.onScroll = () => this.measure();
            this.measure();

            /*
             * And again after a frame.
             *
             * Alpine initialises as soon as the element exists, which can be before the board
             * has been laid out — scrollWidth and clientWidth are then equal, the pager decides
             * there is nothing to page, and it never asks again until something happens to
             * change a size. A board that silently has no controls is what that looks like.
             */
            requestAnimationFrame(() => this.measure());

            const el = this.track();
            // A board with no columns yet is not an error; there is simply nothing to page.
            if (! el) {
                return;
            }

            el.addEventListener('scroll', this.onScroll, { passive: true });
            window.addEventListener('resize', this.onScroll, { passive: true });

            if (typeof ResizeObserver !== 'undefined') {
                this.observer = new ResizeObserver(() => this.measure());

                /*
                 * BOTH boxes. The track's own size changes when the panel opens beside it, and
                 * its CONTENT width changes when columns arrive — and a ResizeObserver on the
                 * track alone misses the second, because adding a column to a scroller does not
                 * resize the scroller.
                 */
                this.observer.observe(el);
                if (this.root) {
                    this.observer.observe(this.root);
                }
            }
        },

        destroy() {
            this.track()?.removeEventListener('scroll', this.onScroll);
            window.removeEventListener('resize', this.onScroll);
            this.observer?.disconnect();
        },

        measure() {
            const el = this.track();
            if (! el) {
                this.overflowing = false;

                return;
            }

            const slack = el.scrollWidth - el.clientWidth;
            this.overflowing = slack > 4;
            this.atStart = el.scrollLeft <= 4;
            // A pixel of rounding at the far end otherwise leaves the button live forever.
            this.atEnd = el.scrollLeft >= slack - 4;
        },

        page(direction) {
            const el = this.track();
            if (! el) {
                return;
            }

            // One column at a time, measured from a real column rather than assumed — the
            // width is a clamp() and changes with the viewport.
            const column = el.querySelector('[data-board-column]');
            const step = column
                ? column.getBoundingClientRect().width + COLUMN_GAP
                : el.clientWidth * 0.8;

            if (el.scrollBy) {
                el.scrollBy({ left: direction * step, behavior: 'smooth' });
            } else {
                el.scrollLeft += direction * step;
            }
        },
    };
}

/** How wide a column is, for centring one that is only partly on screen. */
export function centreOffsetFor(track, column) {
    // Its position WITHIN the scroller, which is not its position on screen: offsetLeft is
    // measured from the offset parent, and both are inside the same one here.
    const centred = column.offsetLeft - (track.clientWidth - column.offsetWidth) / 2;
    const furthest = Math.max(0, track.scrollWidth - track.clientWidth);

    // Clamped, or the first and last columns are asked to sit in the middle of a track that
    // cannot scroll far enough and end up further from view than they started.
    return Math.max(0, Math.min(centred, furthest));
}

function readNumber(key) {
    try {
        const value = parseInt(localStorage.getItem(key) || '', 10);

        return Number.isFinite(value) ? value : null;
    } catch (e) {
        return null;   // a browser refusing storage is not a reason to refuse the pane
    }
}

function writeNumber(key, value) {
    try {
        localStorage.setItem(key, String(value));
    } catch (e) {
        // ignored, deliberately
    }
}
